import React from 'react'
import type { DesktopSuggestion } from '../../dashboardActionsConfig.tsx'
import { GUIDE_ACTION_IDS_WITH_SPECIALIST } from '../../../../../constants/agentNames.ts'
import { guideMessageMatchesSuggestion } from '../../../../../utils/conversationalMode/guideSuggestionActionIds.ts'
import { normalizeGuideSteps, parseGuideContent } from '../../../../../utils/conversationalMode/guideMessageUtils.ts'
import messageStyles from './MessageBubble.module.scss'
import guideStyles from './GuideMessageBubble.module.scss'
import { formatMessageText } from './formatMessageText.tsx'
import type { ChatbotSuggestedAction } from '../../../../../services/llmService.ts'

const GuideMessageBubble: React.FC<{
  messageId: string
  displayedText: string
  isTyping: boolean
  inFloatingAgent: boolean
  suggestion?: DesktopSuggestion | null
  messageActionId?: string
  onSuggestionAction?: () => void
  onRequestGuide?: () => void
  suggestedActions?: ChatbotSuggestedAction[]
  onSuggestedChip?: (label: string, actionId: string) => void
}> = ({
  messageId,
  displayedText,
  isTyping,
  inFloatingAgent,
  suggestion,
  messageActionId,
  onSuggestionAction,
  onRequestGuide,
  suggestedActions,
  onSuggestedChip
}) => {
  const guideContent = parseGuideContent(normalizeGuideSteps(displayedText))
  const { intro, steps, outro, partialStep } = guideContent

  const showActionButton =
    suggestion &&
    guideMessageMatchesSuggestion(messageActionId, suggestion.id) &&
    onSuggestionAction &&
    !isTyping
  const hasSpecialist = messageActionId && GUIDE_ACTION_IDS_WITH_SPECIALIST.includes(messageActionId)
  const showGuideRequestButton = hasSpecialist && onRequestGuide && !isTyping

  return (
    <div
      className={`${messageStyles.messageBubble} ${messageStyles.assistantMessage} ${isTyping ? messageStyles.typing : ''} ${inFloatingAgent ? messageStyles.inFloatingAgent : ''}`}
    >
      <div className={`${messageStyles.messageContent} ${guideStyles.guideMessageContent}`}>
        {intro && (
          <div className={guideStyles.guideMarkdown}>
            {formatMessageText(intro, `${messageId}-guide-intro`)}
          </div>
        )}

        <div className={guideStyles.guideSteps}>
          {steps.map((step, index) => (
            <div key={index} className={guideStyles.guideStep}>
              <div className={`${guideStyles.guideMarkdown} ${guideStyles.stepContent}`}>
                {formatMessageText(step, `${messageId}-guide-step-${index}`)}
              </div>
            </div>
          ))}
          {partialStep && (
            <div className={guideStyles.guideStep}>
              <div className={`${guideStyles.guideMarkdown} ${guideStyles.stepContent}`}>
                {formatMessageText(partialStep, `${messageId}-guide-partial`)}
                {isTyping && <span className={messageStyles.cursor}>|</span>}
              </div>
            </div>
          )}
        </div>

        {outro && (
          <div className={guideStyles.guideMarkdownRow}>
            <div className={guideStyles.guideMarkdown}>
              {formatMessageText(outro, `${messageId}-guide-outro`)}
            </div>
            {isTyping && <span className={messageStyles.cursor}>|</span>}
          </div>
        )}

        {!intro && steps.length === 0 && !partialStep && displayedText && (
          <div className={guideStyles.guideMarkdownRow}>
            <div className={guideStyles.guideMarkdown}>
              {formatMessageText(displayedText, `${messageId}-guide-fallback`)}
            </div>
            {isTyping && <span className={messageStyles.cursor}>|</span>}
          </div>
        )}

        {(showActionButton || showGuideRequestButton) && (
          <div className={guideStyles.guideActionButtons}>
            {showGuideRequestButton && (
              <button
                type="button"
                className={guideStyles.guideRequestButton}
                onClick={onRequestGuide}
                aria-label="Ayudame a hacerlo"
              >
                Ayudame a hacerlo
              </button>
            )}
            {showActionButton && (
              <button
                type="button"
                className={guideStyles.guideActionButton}
                onClick={onSuggestionAction}
                aria-label={suggestion?.label}
              >
                Comenzar →
              </button>
            )}
          </div>
        )}
        {suggestedActions &&
          suggestedActions.length > 0 &&
          onSuggestedChip &&
          !isTyping && (
            <div
              className={messageStyles.suggestedActionsRow}
              role="group"
              aria-label="Acciones sugeridas"
            >
              {suggestedActions.map((a, i) => (
                <button
                  key={`${a.actionId}-${i}`}
                  type="button"
                  className={messageStyles.suggestedActionChip}
                  onClick={() => onSuggestedChip(a.label, a.actionId)}
                >
                  {a.label}
                </button>
              ))}
            </div>
          )}
      </div>
    </div>
  )
}

export default GuideMessageBubble
